// Core type definitions for C64 Arcade Games

export interface Vector2 {
  x: number;
  y: number;
}

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GameConfig {
  width: number;
  height: number;
  fps: number;
  scale: number;
}

export interface GameState {
  scene: SceneType;
  score: number;
  lives: number;
  level: number;
  paused: boolean;
  gameOver: boolean;
}

export enum SceneType {
  MENU = 'menu',
  GAME = 'game',
  GAME_OVER = 'gameOver',
  LEADERBOARD = 'leaderboard',
  SETTINGS = 'settings',
  ABOUT = 'about'
}

export interface GameObject {
  id: string;
  position: Vector2;
  velocity: Vector2;
  size: Vector2;
  active: boolean;
  update(deltaTime: number): void;
  render(ctx: CanvasRenderingContext2D): void;
  getBounds(): Rectangle;
}

export interface GameScene {
  init(): void;
  update(deltaTime: number, input: InputState): void;
  render(ctx: CanvasRenderingContext2D): void;
  cleanup(): void;
  handleInput(input: InputState): void;
}

export interface InputState {
  keys: { [key: string]: boolean };
  touch: TouchState;
  mouse: MouseState;
}

export interface TouchState {
  active: boolean;
  position: Vector2;
  startPosition: Vector2;
  gesture: 'none' | 'tap' | 'swipe' | 'hold';
}

export interface MouseState {
  position: Vector2;
  buttons: { [button: number]: boolean };
}

export interface AudioConfig {
  volume: number;
  muted: boolean;
}

export interface GameInfo {
  id: string;
  title: string;
  description: string;
  highScore: number;
  preview: string;
  controls: string[];
}

export interface LeaderboardEntry {
  initials: string;
  score: number;
  gameId: string;
  timestamp: number;
  level?: number;
}

export interface GameLeaderboard {
  gameId: string;
  entries: LeaderboardEntry[];
}

export interface AllTimeLeaderboard {
  entries: LeaderboardEntry[];
}

export interface LeaderboardData {
  gameLeaderboards: { [gameId: string]: LeaderboardEntry[] };
  allTimeLeaderboard: LeaderboardEntry[];
}

export interface InitialEntryState {
  position: number;
  selectedChar: number;
  initials: string[];
  isComplete: boolean;
}

export const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789 '.split('');

export const GAME_LIST: GameInfo[] = [
  {
    id: 'galactic-defense',
    title: 'GALACTIC DEFENSE',
    description: 'Defend Earth from alien invaders',
    highScore: 15420,
    preview: '▲▲▲▲',
    controls: ['←→ Move', 'SPACE Fire']
  },
  {
    id: 'maze-runner',
    title: 'MAZE RUNNER',
    description: 'Collect dots and avoid ghosts',
    highScore: 8700,
    preview: '●●●',
    controls: ['↑↓←→ Move']
  },
  {
    id: 'defender',
    title: 'DEFENDER',
    description: 'Protect humanoids from alien abduction',
    highScore: 25600,
    preview: '◀═══',
    controls: ['←→ Thrust', '↑↓ Thrust', 'SPACE Fire', 'R Reverse', 'H Hyperspace']
  },
  {
    id: 'centipede',
    title: 'CENTIPEDE',
    description: 'Destroy the centipede before it reaches you',
    highScore: 18900,
    preview: '◉◉◉◉',
    controls: ['↑↓←→ Move', 'SPACE Fire']
  },
  {
    id: 'river-crossing',
    title: 'RIVER CROSSING',
    description: 'Cross roads and rivers safely',
    highScore: 2340,
    preview: '🐸',
    controls: ['↑↓←→ Move']
  },
  {
    id: 'space-debris',
    title: 'SPACE DEBRIS',
    description: 'Destroy asteroids with physics',
    highScore: 9850,
    preview: '◊◊◊',
    controls: ['←→ Rotate', 'W Thrust', 'SPACE Fire']
  },
  {
    id: 'tunnel-blaster',
    title: 'TUNNEL BLASTER',
    description: 'Survive the geometric tunnel',
    highScore: 12600,
    preview: '|||',
    controls: ['←→ Move', 'SPACE Fire']
  },
  {
    id: 'block-drop',
    title: 'BLOCK DROP',
    description: 'Clear lines with falling blocks',
    highScore: 45200,
    preview: '▓▓▓',
    controls: ['←→ Move', '↓ Drop', 'SPACE Rotate']
  },
  {
    id: 'breakout',
    title: 'BREAKOUT',
    description: 'Break all bricks with ball and paddle',
    highScore: 8640,
    preview: '▬▬▬',
    controls: ['←→ Move Paddle', 'SPACE Launch']
  }
];